import { useMemo } from "react";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
  PowerSettingsNew as PowerIcon,
  Inventory as InventoryIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import type { RoofTreeRow } from "../models";
import { StatusIndicator } from "../../../shared/components/StatusIndicator";

const cellStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

interface Props {
  onSetOnline: (row: RoofTreeRow) => void;
  onSetOffline: (row: RoofTreeRow) => void;
}

export const useRoofTreeTableData = ({ onSetOnline, onSetOffline }: Props) => {
  const theme = useTheme();

  const columnDefs: ColDef<RoofTreeRow>[] = useMemo(
    () => [
      {
        headerName: "",
        field: "EquipmentName",
        minWidth: 300,
        flex: 1,
        cellStyle: {
          ...cellStyle,
          justifyContent: "flex-start",
          paddingLeft: theme.spacing(1),
        },
        cellRenderer: (params: ICellRendererParams<RoofTreeRow>) => {
          if (!params.data) return null;

          const { rowType } = params.data;
          const node = params.node;
          const hasChildren =
            node.allChildrenCount && node.allChildrenCount > 0;
          const isExpanded = node.expanded;

          // Calculate indentation based on tree level
          const level = node.level || 0;
          const indentPx = level * 20;

          // Determine display name and icon based on row type
          let displayName: string;
          let icon: React.ReactNode = null;
          let showStatus = false;

          switch (rowType) {
            case "container":
              // Top-level containers (EAF-Online, EAF-Offline)
              displayName = params.data.ContainerName || "";
              const isOnlineContainer = displayName
                .toLowerCase()
                .includes("online");
              icon = isOnlineContainer ? (
                <CheckCircleIcon
                  fontSize="small"
                  sx={{ color: "success.main" }}
                />
              ) : (
                <CancelIcon fontSize="small" sx={{ color: "text.secondary" }} />
              );
              break;

            case "sparesContainer":
              // Spares containers (Roof Spares, Spider Spares, etc.)
              displayName = params.data.ContainerName || "";
              icon = (
                <InventoryIcon
                  fontSize="small"
                  sx={{ color: "warning.main" }}
                />
              );
              break;

            case "equipment":
              // Roof assemblies - no status indicator (heat limits are on panels, not roofs)
              displayName = params.data.EquipmentName || "";
              break;

            case "location":
              // Panel positions on roofs - show only position name
              displayName = params.data.PositionName || "";
              showStatus = true;
              break;

            case "sparePart":
              // Spare panels in spares containers - show SerialNumber Make Model or Make Model
              displayName =
                [
                  params.data.PartInfo?.SerialNumber,
                  params.data.PartInfo?.Make,
                  params.data.PartInfo?.Model,
                ]
                  .filter(Boolean)
                  .join(" ") || "-";
              showStatus = true;
              break;

            default:
              displayName = params.data.EquipmentName || "";
          }

          // Get the StatusCode for heat limit alerts (O=OK, A=Alert, W=Warning, B=Blank)
          const statusCode = params.data.StatusCode;

          const handleExpandClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            node.setExpanded(!isExpanded);
            // Force refresh of this cell to update the icon
            params.api.refreshCells({
              rowNodes: [node],
              columns: [params.column!],
              force: true,
            });
          };

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.5,
                width: "100%",
                height: "100%",
                pl: `${indentPx}px`,
              }}
            >
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={handleExpandClick}
                  sx={{
                    p: 0,
                    color: "text.secondary",
                  }}
                >
                  {isExpanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: 20 }} /> // Spacer for alignment
              )}
              {icon}
              {showStatus ? (
                <StatusIndicator statusCode={statusCode ?? "B"} />
              ) : null}
              <span>{displayName}</span>
            </Box>
          );
        },
      },
      {
        field: "Heats",
        headerName: "Campaign",
        headerTooltip: "Heats since panel was installed (Campaign)",
        width: 110,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          const rowType = params.data?.rowType;
          if (rowType === "container" || rowType === "sparesContainer")
            return "-";
          return params.value != null ? params.value.toLocaleString() : "-";
        },
      },
      {
        field: "HeatsSinceRepair",
        headerName: "Since Last Repair",
        headerTooltip: "Heats since last repair",
        width: 110,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          const rowType = params.data?.rowType;
          // Show "-" for containers
          if (rowType === "container" || rowType === "sparesContainer")
            return "-";
          // Roofs don't have this metric (they aren't repaired, their panels are)
          if (rowType === "equipment") return "-";
          // Show "-" for empty slots
          if (rowType === "location" && !params.data?.HasAssignedPart)
            return "-";
          return params.value != null ? params.value.toLocaleString() : "-";
        },
      },
      {
        field: "TotalHeats",
        headerName: "Total",
        headerTooltip: "Total lifetime heats",
        width: 90,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          const rowType = params.data?.rowType;
          // Show "-" for containers
          if (rowType === "container" || rowType === "sparesContainer")
            return "-";
          // Show "-" for empty slots
          if (rowType === "location" && !params.data?.HasAssignedPart)
            return "-";
          return params.value?.toLocaleString() ?? "0";
        },
      },
      {
        field: "InstalledDate",
        headerName: "Last Action",
        width: 150,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          if (!params.value) return "-";
          return dxtToLocalServerTime(params.value, "MM/dd/yyyy HH:mm");
        },
      },
      {
        headerName: "Days",
        width: 80,
        cellStyle: cellStyle,
        valueGetter: (params) => {
          const rowType = params.data?.rowType;
          // Show "-" for containers
          if (rowType === "container" || rowType === "sparesContainer")
            return "-";
          if (!params.data?.InstalledDate) return "-";
          // Check if parent equipment is offline - Status is now included in all rows
          if (params.data.Status === "Offline") return "-";

          const installedDate = new Date(params.data.InstalledDate);
          const currentDate = new Date();
          const diffTime = Math.abs(
            currentDate.getTime() - installedDate.getTime(),
          );
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          return diffDays.toString();
        },
      },
      {
        headerName: "",
        width: 60,
        cellStyle: cellStyle,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<RoofTreeRow>) => {
          const row = params.data;
          if (!row) return null;

          // Show power button for equipment rows (roofs) based on status
          if (row.rowType === "equipment") {
            const isOnline = row.Status === "Online";

            // Online roof: show Set Offline button
            if (isOnline) {
              return (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="Set Offline">
                    <IconButton
                      size="medium"
                      sx={{ color: "success.main" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetOffline(row);
                      }}
                    >
                      <PowerIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            }

            // Offline roof: show Set Online button
            return (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Set Online">
                  <IconButton
                    size="medium"
                    sx={{ color: "text.secondary" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetOnline(row);
                    }}
                  >
                    <PowerIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          }

          return null;
        },
      },
    ],
    [onSetOnline, onSetOffline, theme],
  );

  const autoGroupColumnDef: ColDef<RoofTreeRow> = useMemo(
    () => ({
      headerName: "",
      width: 0,
      maxWidth: 0,
      minWidth: 0,
      resizable: false,
      sortable: false,
      suppressSizeToFit: true,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: () => null,
      },
    }),
    [],
  );

  return { columnDefs, autoGroupColumnDef };
};
